//@ts-nocheck
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Eye, LayoutTemplate, Box, Type } from 'lucide-react';

import { ProjectType } from '@/types';
import { useUpdateProjectType } from '@/hooks/api/useProjects';
import { ProjectTypeAssetForm } from './AssetsTab';
import { CustomFieldsTab } from './CustomFieldsTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { SimpleForm } from '@/components/SimpleForm';

const projectTypeEditSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  layers: z.array(z.string()).optional(), // Assuming array of IDs
});

type ProjectTypeEditFormData = z.infer<typeof projectTypeEditSchema>;

export const ProjectTypeDetails = ({
  projectType,
}: {
  projectType: ProjectType;
}) => {
  const { updateProjectType, isUpdating } = useUpdateProjectType();

  const form = useForm<ProjectTypeEditFormData>({
    resolver: zodResolver(projectTypeEditSchema),
    defaultValues: {
      name: projectType?.name || '',
      description: projectType?.description || '',
      layers: [],
    },
  });

  // Sync data on change
  useEffect(() => {
    if (projectType) {
      const initialAssets = projectType.layers?.map((pl) => pl.layerId) || [];
      form.reset({
        name: projectType.name,
        description: projectType.description || '',
        layers: initialAssets,
      });
    }
  }, [projectType, form]);

  const onSubmit = (formData: ProjectTypeEditFormData) => {
    const { dirtyFields } = form.formState;

    const payload: any = {
      projectTypeId: projectType.id,
    };

    if (dirtyFields.name) payload.name = formData.name;
    if (dirtyFields.description) payload.description = formData.description;
    if (dirtyFields.layers) payload.layers = formData.layers;

    if (Object.keys(payload).length <= 1) {
      toast.info('No changes to save.');
      return;
    }

    updateProjectType(payload, {
      onSuccess: () => toast.success('Project type saved successfully!'),
      onError: () => toast.error('Error saving project type.'),
    });
  };

  return (
    <SimpleForm
      methods={form}
      onSubmit={onSubmit}
      submitText="Save Changes"
      isSubmitting={isUpdating}
      className="space-y-6"
    >
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-0 border-b border-border/40 bg-muted/10">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary">
              <LayoutTemplate className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>{projectType.name}</CardTitle>
              <CardDescription>
                Configure workflow details and associated assets.
              </CardDescription>
            </div>
          </div>

          {/* TABS HEADER INSIDE CARD */}
          <Tabs defaultValue="general" className="w-full mt-4">
            <TabsList className="w-full justify-start h-auto p-0 bg-transparent border-b border-transparent space-x-6 rounded-none">
              <TabsTrigger
                value="general"
                className={cn(
                  'rounded-none border-b-1 border-transparent px-4 py-3 font-medium text-muted-foreground transition-all hover:text-foreground cursor-pointer data-[state=active]:border-secondary data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:dark:border-secondary data-[state=active]:dark:text-secondary-foreground',
                )}
              >
                <div className="flex items-center gap-2">
                  <LayoutTemplate className="h-4 w-4" />
                  General
                </div>
              </TabsTrigger>

              <TabsTrigger
                value="assets"
                className={cn(
                  'rounded-none border-b-1 border-transparent px-4 py-3 font-medium text-muted-foreground transition-all hover:text-foreground cursor-pointer data-[state=active]:border-secondary data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:dark:border-secondary data-[state=active]:dark:text-secondary-foreground',
                )}
              >
                <div className="flex items-center gap-2">
                  <Box className="h-4 w-4" />
                  Assets
                </div>
              </TabsTrigger>

              <TabsTrigger
                value="fields"
                className={cn(
                  'rounded-none border-b-1 border-transparent px-4 py-3 font-medium text-muted-foreground transition-all hover:text-foreground cursor-pointer data-[state=active]:border-secondary data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:dark:border-secondary data-[state=active]:dark:text-secondary-foreground',
                )}
              >
                <div className="flex items-center gap-2">
                  <Type className="h-4 w-4" />
                  Custom Fields
                </div>
              </TabsTrigger>
            </TabsList>

            {/* TABS CONTENT AREA */}
            <div className="p-6">
              <TabsContent
                value="general"
                className="mt-0 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300"
              >
                <div className="grid gap-6 max-w-2xl">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Type Name</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="e.g. Fiber Installation"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            className="resize-none min-h-[120px]"
                            {...field}
                            placeholder="Describe the purpose of this workflow..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </TabsContent>

              <TabsContent
                value="assets"
                className="mt-0 animate-in fade-in slide-in-from-bottom-2 duration-300"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-foreground">
                      Associated Asset Types
                    </h4>
                    {/* You could add a 'Manage Assets' link here if needed */}
                  </div>
                  <ProjectTypeAssetForm control={form.control} name="layers" />
                </div>
              </TabsContent>

              <TabsContent
                value="fields"
                className="mt-0 animate-in fade-in slide-in-from-bottom-2 duration-300"
              >
                <CustomFieldsTab projectType={projectType} />
              </TabsContent>
            </div>
          </Tabs>
        </CardHeader>
        {/* Card Content is implicitly handled by the Tabs structure above to keep header sticky-ish if needed */}
      </Card>
    </SimpleForm>
  );
};
